const STORAGE_KEY = "un_streaks_v1";

const read = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const items = raw ? JSON.parse(raw) : [];
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
};

const write = (items) => localStorage.setItem(STORAGE_KEY, JSON.stringify(items));

const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const localDateKey = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const keyToDate = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

export const getStreaks = () => read();

export const addStreak = ({ label, intention = "build" }) => {
  const items = read();
  const item = {
    id: uid(),
    label: label.trim(),
    intention,
    createdAt: new Date().toISOString(),
    checkins: {},
  };
  items.unshift(item);
  write(items);
  return item;
};

export const deleteStreak = (id) => {
  const next = read().filter((item) => item.id !== id);
  write(next);
  return next;
};

export const toggleStreakDay = (id, dayKey = localDateKey()) => {
  const next = read().map((item) => {
    if (item.id !== id) return item;
    const checkins = { ...(item.checkins || {}) };
    if (checkins[dayKey]) delete checkins[dayKey];
    else checkins[dayKey] = true;
    return { ...item, checkins };
  });
  write(next);
  return next.find((item) => item.id === id);
};

export const isCompleteToday = (item) =>
  Boolean(item?.checkins?.[localDateKey()]);

export const getCurrentRhythm = (item, now = new Date()) => {
  const checkins = item?.checkins || {};
  let cursor = new Date(now);
  const todayKey = localDateKey(cursor);

  // A streak remains "current" during today even if today's check-in has not
  // happened yet, as long as yesterday was completed. This avoids punishing
  // people first thing in the morning.
  if (!checkins[todayKey]) cursor = addDays(cursor, -1);

  let count = 0;
  while (checkins[localDateKey(cursor)]) {
    count += 1;
    cursor = addDays(cursor, -1);
  }
  return count;
};

export const getBestRhythm = (item) => {
  const keys = Object.keys(item?.checkins || {}).filter(
    (key) => item.checkins[key]
  );
  if (!keys.length) return 0;

  const dates = keys.map(keyToDate).sort((a, b) => a - b);
  let best = 1;
  let run = 1;
  for (let i = 1; i < dates.length; i += 1) {
    const gap = Math.round((dates[i] - dates[i - 1]) / 86400000);
    if (gap === 1) run += 1;
    else if (gap > 1) run = 1;
    best = Math.max(best, run);
  }
  return best;
};

export const getLastSevenDays = (item, now = new Date()) => {
  const checkins = item?.checkins || {};
  return Array.from({ length: 7 }, (_, index) => {
    const date = addDays(now, index - 6);
    return {
      key: localDateKey(date),
      label: date.toLocaleDateString(undefined, { weekday: "narrow" }),
      complete: Boolean(checkins[localDateKey(date)]),
      isToday: localDateKey(date) === localDateKey(now),
    };
  });
};

export const STREAK_SUGGESTIONS = [
  "Meditate",
  "Walk outside",
  "Journal",
  "Hydrate",
  "Read",
  "Stretch",
  "Sleep routine",
  "Creative time",
];
